import { Download } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button } from '../ui/Button.tsx';
import type { ExportResult } from './types.ts';

/**
 * Secondary download button used on /requests, /usage and /billing.
 * Progress lives on the button (`loading` + working copy); cap / error /
 * success report through toasts so the three surfaces stay consistent.
 */
export function ExportExcelButton(props: {
  readonly testId: string;
  readonly disabled?: boolean;
  readonly onExport: () => Promise<ExportResult>;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [working, setWorking] = useState(false);

  async function handleClick(): Promise<void> {
    if (working) return;
    setWorking(true);
    try {
      const result = await props.onExport();
      if (result.status === 'cap') {
        toast.error(t('export.cap'));
        return;
      }
      if (result.status === 'error') {
        toast.error(t('export.error'));
        return;
      }
      toast.success(t('export.done'));
    } catch {
      toast.error(t('export.error'));
    } finally {
      setWorking(false);
    }
  }

  return (
    <Button
      variant="secondary"
      loading={working}
      disabled={props.disabled === true || working}
      onClick={() => {
        void handleClick();
      }}
      data-testid={props.testId}
    >
      {working ? null : <Download aria-hidden="true" className="h-3.5 w-3.5" />}
      {working ? t('export.working') : t('export.idle')}
    </Button>
  );
}
